import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { recentTabsStore } from '@hrms/common/store/recentTabsStore';
import { useBreadcrumbStore } from '@hrms/common/store/useBreadcrumbStore';
import { MenuTypes } from '@zeptonow/zui';
import { ModuleRoutesInterface } from '../header/types';
import { findNode } from '../header/utils';
import { MAX_RECENT_TABS } from './constants';
import { generateImage } from '@hrms/common/utils/generateImage';

interface TabItem {
  urlPattern: string;
  label: string;
  isPinned: boolean;
  url: string;
}

const TabComponent = memo(
  ({
    tab,
    isActive,
    onTabClick,
    onTogglePin,
    onDelete,
  }: {
    tab: TabItem;
    isActive: boolean;
    onTabClick: (e: React.MouseEvent, url: string) => void;
    onTogglePin: (e: React.MouseEvent, urlPattern: string) => void;
    onDelete: (e: React.MouseEvent, urlPattern: string) => void;
  }) => {
    const isPinned = tab.isPinned;
    const activeClass = isActive ? '!bg-primary-25' : '!bg-gray-100';
    const pinBgClass = isActive ? '!bg-primary-300' : '!bg-gray-500';
    const unpinIcon = isActive ? 'unpin-primary-300' : 'unpin-gray-500';
    return (
      <div
        key={tab.urlPattern}
        className={`flex-shrink-0 flex items-center gap-2 p-2 rounded-lg ${activeClass}`}
      >
        <div
          className={`flex-shrink-0 flex-grow-1 cursor-pointer flex items-center justify-center p-[6px] ${
            isPinned ? `rounded-[0.25rem] ${pinBgClass}` : ''
          }`}
          onClick={(e) => onTogglePin(e, tab.urlPattern)}
        >
          {generateImage({
            name: isPinned ? 'pin' : unpinIcon,
            alt: isPinned ? 'pin_icon' : 'unpin_icon',
            className: 'h-4 w-4',
          })}
        </div>
        <div
          className={`flex-shrink-0 flex-grow-1 text-sm cursor-pointer whitespace-nowrap pr-[6px] ${
            isActive ? 'text-primary-300 font-semibold' : ''
          }`}
          onClick={(e) => onTabClick(e, tab.url)}
        >
          {tab.label}
        </div>
        {!isActive && (
          <div
            className="flex-shrink-0 flex-grow-1 cursor-pointer pr-[6px]"
            onClick={(e) => onDelete(e, tab.urlPattern)}
          >
            {generateImage({
              name: 'close',
              alt: 'delete_icon',
              className: 'h-3 w-3',
            })}
          </div>
        )}
      </div>
    );
  },
);

const RecentTabs = ({
  filteredRoutes,
}: {
  filteredRoutes: ModuleRoutesInterface[] | undefined;
}) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);
  const { recentTabs, setRecentTabs } = recentTabsStore();

  const location = useLocation();
  const navigate = useNavigate();
  const { breadcrumbDetails, setBreadcrumbDetails } = useBreadcrumbStore();

  // Memoize tabs by pinned status
  const { pinnedTabs, unpinnedTabs } = useMemo(
    () => ({
      pinnedTabs: recentTabs.filter((tab) => tab.isPinned),
      unpinnedTabs: recentTabs.filter((tab) => !tab.isPinned),
    }),
    [recentTabs],
  );

  // Handle current route detection and add to tabs
  useEffect(() => {
    if (
      location.pathname ||
      (breadcrumbDetails && breadcrumbDetails !== 'loading')
    ) {
      const currentRoute = findNode(
        'route',
        location.pathname,
        filteredRoutes as MenuTypes[],
      );
      if (currentRoute) {
        handleAddTag({
          urlPattern: currentRoute?.route,
          label: `${currentRoute?.title}${
            breadcrumbDetails ? ' . ' + breadcrumbDetails : ''
          }`,
          isPinned: false,
          url: `${location.pathname}${location.search}`,
        });
      }
    }
  }, [location.pathname, location.search, breadcrumbDetails]);

  // Update scroll arrows
  const updateScrollArrows = useCallback(() => {
    const container = scrollContainerRef.current;
    if (container) {
      setShowLeftArrow(container.scrollLeft > 0);
      setShowRightArrow(
        Math.ceil(container.scrollLeft) <
          container.scrollWidth - container.clientWidth,
      );
    }
  }, []);

  useEffect(() => {
    updateScrollArrows();
  }, [updateScrollArrows, recentTabs]);

  // Scroll handlers
  const handleScroll = useCallback(() => {
    updateScrollArrows();
  }, [updateScrollArrows]);

  const scrollLeft = useCallback(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollLeft -= 100;
    }
  }, []);

  const scrollRight = useCallback(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollLeft += 100;
    }
  }, []);

  // Tab action handlers
  const handleTagClick = useCallback(
    (e: React.MouseEvent, url: string) => {
      e.stopPropagation();
      const clickedTagIdx = unpinnedTabs.findIndex((tab) => tab.url === url);
      if (clickedTagIdx > 0) {
        const updatedTabs = [...unpinnedTabs];
        updatedTabs.splice(clickedTagIdx, 1);
        updatedTabs.unshift(unpinnedTabs[clickedTagIdx]);
        setRecentTabs([...pinnedTabs, ...updatedTabs]);
      }

      if (location.pathname + location.search !== url) {
        setBreadcrumbDetails('');
        navigate(url);
      }
    },
    [
      navigate,
      unpinnedTabs,
      pinnedTabs,
      setRecentTabs,
      setBreadcrumbDetails,
      location,
    ],
  );

  const handleTogglePinClick = useCallback(
    (e: React.MouseEvent, urlPattern: string) => {
      e.stopPropagation();
      setRecentTabs(
        recentTabs.map((tab) =>
          tab.urlPattern === urlPattern
            ? { ...tab, isPinned: !tab.isPinned }
            : tab,
        ),
      );
    },
    [recentTabs, setRecentTabs],
  );

  const handleDeleteClick = useCallback(
    (e: React.MouseEvent, urlPattern: string) => {
      e.stopPropagation();
      setRecentTabs(recentTabs.filter((tab) => tab.urlPattern !== urlPattern));
    },
    [recentTabs, setRecentTabs],
  );

  const handleAddTag = useCallback(
    (newTag: TabItem) => {
      const tabIndex = recentTabs.findIndex(
        (tab) => tab.urlPattern === newTag.urlPattern,
      );

      if (tabIndex === -1) {
        if (recentTabs.length < MAX_RECENT_TABS) {
          setRecentTabs([...pinnedTabs, newTag, ...unpinnedTabs]);
        } else {
          // Remove the last unpinned tab if at max capacity
          const updatedUnpinned = unpinnedTabs.slice(0, -1);
          setRecentTabs([...pinnedTabs, newTag, ...updatedUnpinned]);
        }
      } else {
        let updatedTabs = [...recentTabs];
        const currentTab = recentTabs[tabIndex];
        // Only update needed properties
        if (
          newTag.url !== currentTab.url ||
          newTag.label !== currentTab.label
        ) {
          updatedTabs = updatedTabs.map((tab) =>
            tab.urlPattern === newTag.urlPattern
              ? { ...tab, url: newTag.url, label: newTag.label }
              : tab,
          );
        }
        // Move to front if not pinned
        if (!currentTab.isPinned) {
          const [movedTab] = updatedTabs.splice(tabIndex, 1);
          updatedTabs.unshift(movedTab);
        }
        setRecentTabs(updatedTabs);
      }
    },
    [recentTabs, pinnedTabs, unpinnedTabs, setRecentTabs],
  );

  const isActiveTab = useCallback(
    (url: string) => location.pathname + location.search === url,
    [location.pathname, location.search],
  );

  return (
    <div className="flex items-center w-full h-14 px-2 border-t border-b border-gray-200">
      {
        <button
          className={`p-2 mr-2 border border-gray-300 rounded-full ${
            showLeftArrow ? '' : 'invisible'
          }`}
          onClick={scrollLeft}
        >
          {generateImage({
            name: 'arrow-left-1',
            alt: 'left_icon',
            className: 'h-3 w-3',
          })}
        </button>
      }
      <div
        className="flex items-center overflow-scroll gap-2 flex-1 hide-scrollbar"
        ref={scrollContainerRef}
        onScroll={handleScroll}
      >
        {pinnedTabs.concat(unpinnedTabs).map((tab) => (
          <TabComponent
            key={tab.urlPattern}
            tab={tab}
            isActive={isActiveTab(tab.url)}
            onTabClick={handleTagClick}
            onTogglePin={handleTogglePinClick}
            onDelete={handleDeleteClick}
          />
        ))}
      </div>
      <button
        className={`p-2 ml-2 border border-gray-300 rounded-full ${
          showRightArrow ? '' : 'invisible'
        }`}
        onClick={scrollRight}
      >
        {generateImage({
          name: 'arrow-right',
          alt: 'right_icon',
          className: 'h-3 w-3',
        })}
      </button>
    </div>
  );
};

export default memo(RecentTabs);
